import {
  CalendarClock,
  Clock3,
  Laptop,
  LoaderCircle,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  errorMessage,
  type ServiceBookingPolicy,
  type ServiceOffering,
} from "../api";
import { useCity } from "../city";
import { useWorldExperience } from "../world/WorldExperienceProvider";

const emptyPolicy: ServiceBookingPolicy = {
  enabled: true,
  minimumLeadHours: 24,
  maximumHorizonDays: 180,
  maximumRequestedHours: 12,
  slotMinutes: 15,
};

const priceLabel = (service: ServiceOffering) => {
  if (service.startingPriceBdtPaisa != null) {
    return `From ৳${(service.startingPriceBdtPaisa / 100).toLocaleString()}`;
  }
  if (service.startingPriceUsdCents != null) {
    return `From $${(service.startingPriceUsdCents / 100).toFixed(2)}`;
  }
  return "Quote required";
};

const modeIcon = (mode: string) =>
  mode === "remote" ? <Laptop /> : <MapPin />;
const serviceSceneClass = (index: number) => `service-scene-${(index % 4) + 1}`;

export function ServicesPage() {
  const { user } = useCity();
  const { mode: worldMode } = useWorldExperience();
  const [services, setServices] = useState<ServiceOffering[]>([]);
  const [policy, setPolicy] = useState<ServiceBookingPolicy>(emptyPolicy);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api<{
        services: ServiceOffering[];
        policy: ServiceBookingPolicy;
      }>("/v1/services");
      setServices(result.services);
      setPolicy(result.policy);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const categories = useMemo(
    () => ["All", ...new Set(services.map((service) => service.category))],
    [services],
  );
  const visible =
    category === "All"
      ? services
      : services.filter((service) => service.category === category);

  return (
    <div className="page-wrap services-page world-destination-page">
      <section className="services-hero destination-hero service-destination">
        <div>
          <div className="destination-signal">
            <span>Service Pavilion</span>
            <i />
            <small>Upper village road · owner-operated missions</small>
          </div>
          <span className="eyebrow">Service District</span>
          <h1>Request precise human work.</h1>
          <p>
            Travel here for operational reviews, system guidance and controlled
            implementation. Nothing is scheduled or charged until the owner
            reviews the mission and you accept a written quote.
          </p>
          <div className="services-hero-actions">
            <Link
              className="button primary"
              to={user ? "/bookings" : "/profile"}
            >
              <CalendarClock />
              {user ? "Open My Bookings" : "Sign in to request"}
            </Link>
            <a className="button secondary" href="#service-catalogue">
              <Wrench /> Explore services
            </a>
          </div>
        </div>
        <aside>
          <article>
            <ShieldCheck />
            <strong>Owner</strong>
            <span>reviews every request</span>
          </article>
          <article>
            <Clock3 />
            <strong>{policy.minimumLeadHours}h</strong>
            <span>minimum lead time</span>
          </article>
          <article>
            <CalendarClock />
            <strong>{policy.maximumHorizonDays}d</strong>
            <span>request horizon</span>
          </article>
        </aside>
      </section>

      <section className="service-policy-strip">
        <div>
          <ShieldCheck />
          <span>A request is not a reservation.</span>
        </div>
        <div>
          <CalendarClock />
          <span>Only an accepted quote can be scheduled.</span>
        </div>
        <div>
          <Wrench />
          <span>Payment is never assumed or fabricated.</span>
        </div>
      </section>

      <section id="service-catalogue" className="service-catalogue">
        <div className="service-toolbar">
          <div>
            {categories.map((item) => (
              <button
                key={item}
                className={category === item ? "active" : ""}
                onClick={() => setCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <button onClick={() => void load()}>
            <RefreshCw /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="academy-state">
            <LoaderCircle className="spin" />
            <span>Opening the Service District…</span>
          </div>
        ) : error ? (
          <div className="academy-state error">
            <Wrench />
            <span>{error}</span>
            <button onClick={() => void load()}>Retry</button>
          </div>
        ) : visible.length ? (
          <div className="service-grid">
            {visible.map((service, index) => (
              <Link
                className={`service-card ${
                  worldMode === "off"
                    ? "service-scene-off"
                    : serviceSceneClass(index)
                }`}
                to={`/services/${service.slug}`}
                key={service.id}
              >
                <div
                  className={`service-card-art service-tone-${(index % 4) + 1}`}
                >
                  {modeIcon(service.deliveryMode)}
                  <span>{service.deliveryMode}</span>
                </div>
                <div>
                  <div className="service-card-meta">
                    <span>{service.category}</span>
                    <span>
                      <Clock3 /> {service.durationMinutes} min
                    </span>
                  </div>
                  <h2>{service.title}</h2>
                  <p>{service.summary}</p>
                  <footer>
                    <strong>{priceLabel(service)}</strong>
                    <span>
                      Request scope <span aria-hidden="true">→</span>
                    </span>
                  </footer>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="academy-state">
            <Wrench />
            <span>No published services are available in this category.</span>
          </div>
        )}
      </section>
    </div>
  );
}
